import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { SOCIAL_XP_VALUES, getXPForAction } from '../constants/socialXP';

export type SocialActionType =
  | 'like' | 'save' | 'view' | 'react'
  | 'comment' | 'reply' | 'message' | 'storyReact' | 'encourage'
  | 'post' | 'story' | 'share' | 'copy' | 'upload'
  | 'guildPost' | 'questJoin' | 'eventRsvp' | 'projectContribute' | 'libraryAdd'
  | 'guildCreate' | 'eventHost' | 'mentor' | 'pactCreate' | 'questCreate' | 'moderate';

export type SocialActionCategory = 'micro' | 'communication' | 'contribution' | 'collaborative' | 'leadership';

interface AwardSocialXPParams {
  actionType: SocialActionType;
  userId?: string;
  qualityScore?: number;
  postId?: string;
  commentId?: string;
  storyId?: string;
  guildId?: string;
  pactId?: string;
}

export function getActionCategory(actionType: SocialActionType): SocialActionCategory {
  if (['like', 'save', 'view', 'react'].includes(actionType)) return 'micro';
  if (['comment', 'reply', 'message', 'storyReact', 'encourage'].includes(actionType)) return 'communication';
  if (['post', 'story', 'share', 'copy', 'upload'].includes(actionType)) return 'contribution';
  if (['guildPost', 'questJoin', 'eventRsvp', 'projectContribute', 'libraryAdd'].includes(actionType)) return 'collaborative';
  if (['guildCreate', 'eventHost', 'mentor', 'pactCreate', 'questCreate', 'moderate'].includes(actionType)) return 'leadership';
  return 'micro';
}

export async function awardSocialXP({
  actionType,
  userId,
  qualityScore = 1.0,
  postId,
  commentId,
  storyId,
  guildId,
  pactId,
}: AwardSocialXPParams): Promise<number> {
  try {
    let targetUserId = userId;

    if (!targetUserId) {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        console.error('No authenticated user found');
        return 0;
      }
      targetUserId = user.id;
    }

    const category = getActionCategory(actionType);
    const baseXP = getXPForAction(category, actionType);

    if (baseXP === 0) {
      console.warn(`No XP value found for action: ${actionType}`);
      return 0;
    }

    const { data, error } = await supabase.rpc('award_social_xp', {
      p_user_id: targetUserId,
      p_action_type: actionType,
      p_base_xp: baseXP,
      p_quality_score: qualityScore,
      p_post_id: postId || null,
      p_comment_id: commentId || null,
      p_story_id: storyId || null,
      p_guild_id: guildId || null,
      p_pact_id: pactId || null,
    });

    if (error) {
      console.error('Error awarding Social XP:', error);
      return 0;
    }

    const awarded = (data as number | null) || 0;
    /* ⭐⭐ THE LEVEL MOVES WHEN YOU ACT (owner, 2026-10-04). The server recorded the XP
       just now, but the app's social total (`socialStats`, which the overall level
       sums) was refreshed only when Social was OPENED — so a post's XP appeared later,
       on the next visit, and looked like "XP for visiting Social". Refresh it now —
       for your OWN awards only. */
    if (awarded > 0 && !userId) refreshLocalSocialTotal();
    return awarded;
  } catch (error) {
    console.error('Exception awarding Social XP:', error);
    return 0;
  }
}

/* ⚠️ `socialStore` imports this module, so a static import would be a cycle; by the
   time anything is awarded the store is loaded, and this resolves from memory. */
function refreshLocalSocialTotal(): void {
  void import('../stores/socialStore')
    .then(m => m.useSocialStore.getState().refreshSocialStats())
    .catch(() => { /* the next startup or Social visit still refreshes it */ });
}

export async function getSocialDomainStats(userId?: string) {
  try {
    let targetUserId = userId;

    if (!targetUserId) {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;
      targetUserId = user.id;
    }

    const { data, error } = await supabase
      .from('domain_stats')
      .select('*')
      .eq('user_id', targetUserId)
      .eq('domain_id', 'social')
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching social domain stats:', error);
      return null;
    }

    return data || null;
  } catch (error) {
    console.error('Exception fetching social domain stats:', error);
    return null;
  }
}

export async function getSocialXPHistory(userId?: string, limit = 50) {
  try {
    let targetUserId = userId;

    if (!targetUserId) {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return [];
      targetUserId = user.id;
    }

    const { data, error } = await supabase
      .from('social_xp_actions')
      .select('*')
      .eq('user_id', targetUserId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching social XP history:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception fetching social XP history:', error);
    return [];
  }
}

export async function getSocialXPLeaderboard(
  scope: 'global' | 'guild' = 'global',
  guildId?: string,
  limit = 50
) {
  try {
    let query = supabase
      .from('domain_stats')
      .select(`
        *,
        user_profiles!inner(name, avatar_url)
      `)
      .eq('domain_id', 'social')
      .order('points', { ascending: false })
      .limit(limit);

    if (scope === 'guild' && guildId) {
      const { data: memberIds } = await supabase
        .from('guild_members')
        .select('user_id')
        .eq('guild_id', guildId);

      if (memberIds && memberIds.length > 0) {
        const userIds = memberIds.map(m => m.user_id);
        query = query.in('user_id', userIds);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching social XP leaderboard:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception fetching social XP leaderboard:', error);
    return [];
  }
}

export async function getActiveMultipliers(userId?: string) {
  try {
    let targetUserId = userId;

    if (!targetUserId) {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return [];
      targetUserId = user.id;
    }

    const { data, error } = await supabase
      .from('social_xp_multipliers')
      .select('*')
      .eq('user_id', targetUserId)
      .eq('is_active', true)
      .or('expires_at.is.null,expires_at.gt.now()')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching active multipliers:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception fetching active multipliers:', error);
    return [];
  }
}

export async function calculateTotalMultiplier(userId?: string): Promise<number> {
  try {
    let targetUserId = userId;

    if (!targetUserId) {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return 1.0;
      targetUserId = user.id;
    }

    const { data, error } = await supabase.rpc('calculate_social_multiplier', {
      p_user_id: targetUserId,
    });

    if (error) {
      console.error('Error calculating social multiplier:', error);
      return 1.0;
    }

    return data || 1.0;
  } catch (error) {
    console.error('Exception calculating social multiplier:', error);
    return 1.0;
  }
}

interface SocialXPEarnedEvent {
  xpEarned: number;
  actionType: SocialActionType;
  totalXP: number;
  newLevel?: number;
  newTitle?: string;
}

const socialXPListeners: ((event: SocialXPEarnedEvent) => void)[] = [];

export function onSocialXPEarned(callback: (event: SocialXPEarnedEvent) => void) {
  socialXPListeners.push(callback);
  return () => {
    const index = socialXPListeners.indexOf(callback);
    if (index > -1) {
      socialXPListeners.splice(index, 1);
    }
  };
}

export function notifySocialXPEarned(event: SocialXPEarnedEvent) {
  socialXPListeners.forEach(listener => listener(event));
}
